import { Button, InputOTP, InputOTPGroup, InputOTPSlot, InputOTPSeparator } from "@heroui/react";
import { REGEXP_ONLY_DIGITS } from "input-otp";
import { useEffect, useRef, useState } from "react";
import { useController, useForm } from "react-hook-form";
import Input from "../input";
import {
  useStartEmailLoginMutation,
  useStartLoginMutation,
  useUpdateUserMutation,
  useVerifyEmailLoginMutation,
  useVerifyLoginMutation,
} from "../../reducers/apiSlice";
import { useToast } from "../ui/toast";
import convertErrorMessage from "../../utils/convertErrorMessage";
import type { LoginFormFlowClassNames } from "./loginFormFlow.types";

export interface LoginFormFlowProps {
  classNames: LoginFormFlowClassNames;
  /** Called after verification succeeds, or after the optional profile step if it is shown. */
  uponVerificationComplete?: () => void;
}

interface LoginForm {
  phone: string;
  verification_code: string;
  name: string;
  email: string;
}

/* UI-DESIGN: emailInput/emailVerifying are the email fallback branch reached
   from the phone-code screen; phone stages are untouched */
type FormStage = "inputting" | "verifying" | "emailInput" | "emailVerifying" | "personal";

const PHONE_PATTERN = /^\+1\d{10}$/;

function isValidPhone(value: string) {
  return PHONE_PATTERN.test(value.trim());
}

export function formatPhoneForDisplay(phone: string) {
  const digits = phone.replace(/\D/g, "");
  if (digits.length < 4) return phone;
  const last4 = digits.slice(-4);
  return `(•••) •••-${last4}`;
}

const LoginFormFlow = ({ classNames: cn, uponVerificationComplete }: LoginFormFlowProps) => {
  const [formStage, setFormStage] = useState<FormStage>("inputting");
  /* UI-DESIGN: new accounts have no email on file, so the email-verification
     fallback is hidden for them (flag comes from the send-code response) */
  const [hasEmail, setHasEmail] = useState(false);
  const prevPhoneValid = useRef(false);
  const suppressAutoAdvanceRef = useRef(false);
  const [startLogin, { isLoading: isStarting }] = useStartLoginMutation();
  const [verifyLogin, { isLoading: isVerifying }] = useVerifyLoginMutation();
  const [startEmailLogin, { isLoading: isStartingEmail }] = useStartEmailLoginMutation();
  const [verifyEmailLogin, { isLoading: isVerifyingEmail }] = useVerifyEmailLoginMutation();
  const [updateUser, {isLoading: isUpdating}] = useUpdateUserMutation();
  const { triggerToast } = useToast();

  const form = useForm<LoginForm>({
    defaultValues: {
      phone: "",
      verification_code: "",
      name: "",
      email: "",
    },
  });

  const phoneValue = form.watch("phone");
  const emailValue = form.watch("email");

  console.log(form.formState.errors);
  const isOtpStage = formStage === "verifying" || formStage === "emailVerifying";

  const {
    field: otpField,
    fieldState: otpFieldState,
  } = useController({
    control: form.control,
    name: "verification_code",
    rules: {
      required: "Enter the code we sent you.",
      minLength: { value: 6, message: "Enter all 6 digits." },
    },
    disabled: !isOtpStage,
  });

  useEffect(() => {
    const valid = isValidPhone(phoneValue ?? "");

    if (formStage !== "inputting") {
      prevPhoneValid.current = valid;
      return;
    }


    if (suppressAutoAdvanceRef.current) {
      if (!valid) suppressAutoAdvanceRef.current = false;
      prevPhoneValid.current = valid;
      return;
    }

    if (valid && !prevPhoneValid.current) {
      startLogin(phoneValue.trim())
        .unwrap()
        .then((body) => {
          setHasEmail(body?.has_email === true);
          setFormStage("verifying");
        })
        .catch((err) => {
          triggerToast({
            title: "Error",
            description: convertErrorMessage(err),
          });
        });
    }
    prevPhoneValid.current = valid;
  }, [phoneValue, formStage, startLogin, triggerToast]);

  /* shared post-verify handling for both phone and email verification */
  const handleVerified = (body: { user?: { first_name?: string; last_name?: string } }) => {
    triggerToast({
      title: "Success",
      description: "You are signed in.",
    });
    if (body?.user?.first_name === "" || body?.user?.last_name === "") {
      setFormStage("personal");
    } else {
      uponVerificationComplete?.();
    }
  };

  const onSubmit = (data: LoginForm) => {
    console.log(data);
    if (formStage === "inputting") {
      startLogin(data.phone.trim())
        .unwrap()
        .then((body) => {
          setHasEmail(body?.has_email === true);
          setFormStage("verifying");
        })
        .catch((err) => {
          triggerToast({
            title: "Error",
            description: convertErrorMessage(err),
          });
        });
      return;
    }

    if (formStage === "verifying") {
      verifyLogin({ phone: phoneValue.trim(), verification_code: data.verification_code })
        .unwrap()
        .then(handleVerified)
        .catch((err) => {
          triggerToast({
            title: "Error",
            description: convertErrorMessage(err),
          });
        });
    }

    if (formStage === "emailInput") {
      startEmailLogin({ phone: phoneValue.trim(), email: data.email.trim() })
        .unwrap()
        .then(() => {
          form.setValue("verification_code", "");
          form.clearErrors("verification_code");
          setFormStage("emailVerifying");
        })
        .catch((err) => {
          triggerToast({
            title: "Error",
            description: convertErrorMessage(err),
          });
        });
      return;
    }

    if (formStage === "emailVerifying") {
      verifyEmailLogin({ phone: phoneValue.trim(), verification_code: data.verification_code })
        .unwrap()
        .then(handleVerified)
        .catch((err) => {
          triggerToast({
            title: "Error",
            description: convertErrorMessage(err),
          });
        });
    }

    if (formStage == "personal") {
      const nameParts = data.name.split(" ");
      const first_name = nameParts[0];
      const last_name = nameParts.length > 1 ? nameParts[1] : "";
      updateUser({ first_name: first_name, last_name: last_name, email: data.email })
        .unwrap()
        .then(() => {
          triggerToast({
            title: "Success",
            description: "Your information has been updated.",
          });
          uponVerificationComplete?.();
        })
        .catch((err) => {
          triggerToast({
            title: "Error",
            description: convertErrorMessage(err),
          });
        });
    }
  };

  const handleBack = () => {
    suppressAutoAdvanceRef.current = true;
    setFormStage("inputting");
    form.setValue("verification_code", "");
    form.clearErrors("verification_code");
  };

  return (
    <>
      <p className={cn.subtitle}>
        {formStage === "inputting"
          ? "Use your phone number to receive a one-time verification code."
          : formStage === "verifying"
            ? `We sent a code to ${formatPhoneForDisplay(phoneValue)}.`
            : formStage === "emailInput"
              ? "Enter the email address on your account to receive a code instead."
              : formStage === "emailVerifying"
                ? `We sent a code to ${emailValue}.`
                : "Enter your name and email address to complete your profile."}
      </p>
      <form className={cn.form} onSubmit={form.handleSubmit(onSubmit)} noValidate>
        {formStage === "inputting" && (
          <Input
            label="Phone number"
            labelClassName={cn.fieldLabel}
            register={form.register("phone", {
              required: "Phone number is required",
              validate: (value: string) => {
                if (!isValidPhone(value)) {
                  return "Use +1 followed by 10 digits.";
                }
                return true;
              },
            })}
            className="w-full"
            placeholder="+1 (555) 000-0000"
            error={form.formState.errors.phone?.message}
            autoComplete="tel"
            inputMode="tel"
          />
        )}
        {formStage === "emailInput" && (
          <Input
            label="Email address"
            type="email"
            labelClassName={cn.fieldLabel}
            register={form.register("email", {
              required: "Email address is required",
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: "Enter a valid email address.",
              },
            })}
            className="w-full"
            placeholder="you@example.com"
            error={form.formState.errors.email?.message}
            autoComplete="email"
            inputMode="email"
          />
        )}
        {(formStage === "verifying" || formStage === "emailVerifying") && (
          <div className={cn.otpRoot}>
            <InputOTP
              maxLength={6}
              pattern={REGEXP_ONLY_DIGITS}
              value={otpField.value ?? ""}
              onChange={otpField.onChange}
              onBlur={otpField.onBlur}
              name={otpField.name}
              ref={otpField.ref}
              isInvalid={!!otpFieldState.error}
              aria-label="Verification code"
            >
              <InputOTPGroup>
                <InputOTPSlot index={0} className={cn.otpSlot} />
                <InputOTPSlot index={1} className={cn.otpSlot} />
                <InputOTPSlot index={2} className={cn.otpSlot} />
              </InputOTPGroup>
              <InputOTPSeparator />
              <InputOTPGroup>
                <InputOTPSlot index={3} className={cn.otpSlot} />
                <InputOTPSlot index={4} className={cn.otpSlot} />
                <InputOTPSlot index={5} className={cn.otpSlot} />
              </InputOTPGroup>
            </InputOTP>
          </div>
        )}
        {(formStage === "verifying" || formStage === "emailVerifying") && otpFieldState.error?.message && (
          <p className={cn.verificationError} role="alert">
            {otpFieldState.error.message}
          </p>
        )}
        {formStage === "personal" && (
          /* UI-DESIGN: profile fields need the same rhythm as separate form stages. */
          <div className={cn.form}>
            <Input
              label="Name"
              labelClassName={cn.fieldLabel}
              register={form.register("name")}
            />
            <Input
              label="Email Address"
              type="email"
              labelClassName={cn.fieldLabel}
              register={form.register("email")}
            />
          </div>
        )}
        <div className={cn.actions}>
          {formStage === "inputting" ? (
            <Button
              type="submit"
              variant="primary"
              className={cn.primaryButton}
              isDisabled={isStarting}
            >
              {isStarting ? "Sending…" : "Send verification code"}
            </Button>
          ) : formStage === "verifying" ? (
            <>
              <Button
                type="submit"
                variant="primary"
                className={cn.primaryButton}
                isDisabled={isVerifying}
              >
                {isVerifying ? "Verifying…" : "Verify and sign in"}
              </Button>
              <Button
                type="button"
                variant="ghost"
                className={cn.backLink}
                onPress={handleBack}
                isDisabled={isVerifying}
              >
                Use a different number
              </Button>
              {/* UI-DESIGN: email fallback entry point — footer text under the
                  existing actions; hidden for accounts without an email on file */}
              {hasEmail ? (
                <Button
                  type="button"
                  variant="ghost"
                  className={cn.backLink}
                  onPress={() => setFormStage("emailInput")}
                  isDisabled={isVerifying}
                >
                  Verify with email address instead
                </Button>
              ) : null}
            </>
          ) : formStage === "emailInput" ? (
            <>
              <Button
                type="submit"
                variant="primary"
                className={cn.primaryButton}
                isDisabled={isStartingEmail}
              >
                {isStartingEmail ? "Sending…" : "Send code to email"}
              </Button>
              <Button
                type="button"
                variant="ghost"
                className={cn.backLink}
                onPress={() => setFormStage("verifying")}
                isDisabled={isStartingEmail}
              >
                Back to phone verification
              </Button>
            </>
          ) : formStage === "emailVerifying" ? (
            <>
              <Button
                type="submit"
                variant="primary"
                className={cn.primaryButton}
                isDisabled={isVerifyingEmail}
              >
                {isVerifyingEmail ? "Verifying…" : "Verify and sign in"}
              </Button>
              <Button
                type="button"
                variant="ghost"
                className={cn.backLink}
                onPress={() => setFormStage("emailInput")}
                isDisabled={isVerifyingEmail}
              >
                Use a different email
              </Button>
            </>
            ) : (
              <Button
                type="submit"
                variant="primary"
                className={cn.primaryButton}
                isDisabled={isUpdating}
              >
                Submit
              </Button>
          )}
        </div>
      </form>
    </>
  );
};

export default LoginFormFlow;
